import { useMemo } from 'react';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import type { DataTableMode } from '@odo/shared-ui';
import type { IteroPatient } from '../../../../services/iteroBrowse';
import { BrowseBody } from './BrowseBody';
import { PaneMessage } from './BrowserPrimitives';
import { tableLayout, tableRows, visiblePaneKey } from './paneVisibility';
import { IteroPatientsTable } from './IteroPatientsTable';
import { OrderView } from './order-view/OrderView';
import { PaneLayout } from './pane/PaneLayout';
import { PaneRestoreBody, PaneRestoreState } from './pane/PaneRestoreState';
import { identityFromPatient } from './pane/paneIdentity';
import { PatientProfile } from './patient-profile/PatientProfile';
import { TableLoadMoreError } from './TableStates';
import type { IteroTabNav } from './useIteroBrowserNavigation';
import { usePatientSelection, type PatientSelection } from './usePatientSelection';
import type { PatientsBrowse } from './usePatientsBrowse';
import { useSelectionPin } from './useSelectionPin';

function PatientsDisabledState({ onGoToOrders }: { onGoToOrders: () => void }) {
    return (
        <div className="flex-1 min-h-0">
            <PaneMessage
                icon={NoSymbolIcon}
                title="Patient search isn't enabled for ODO's iTero integration yet. Align needs to switch on Patient Management. Orders are still available."
            >
                <button
                    type="button"
                    onClick={onGoToOrders}
                    className="px-4 py-2 text-sm font-semibold text-white bg-gray-800 rounded-lg hover:bg-gray-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500"
                >
                    Go to Orders
                </button>
            </PaneMessage>
        </div>
    );
}

interface SelectionDetailsProps {
    selection: PatientSelection;
    patient: IteroPatient;
    today: string;
}

const patientKey = (patient: IteroPatient) => patient.id;

function DrilledOrder({ selection }: { selection: PatientSelection }) {
    const { order, orderTab, motion } = selection;
    if (order.kind !== 'ready' || orderTab === null) {
        return <PaneRestoreBody restored={order} label="scan" />;
    }
    return (
        <OrderView
            order={order.value}
            tab={orderTab}
            onTabChange={selection.setOrderTab}
            focusOnMount={motion.slide !== 'none'}
            onBack={selection.stepBack}
        />
    );
}

// The patient record drives the identity bar in both views, so it stays put when navigating.
function SelectionDetails({ selection, patient, today }: SelectionDetailsProps) {
    const identity = useMemo(() => identityFromPatient(patient), [patient]);
    const { motion, viewKey } = selection;

    return (
        <PaneLayout identity={identity} today={today} viewKey={viewKey} slide={motion.slide}>
            {selection.orderTab !== null ? (
                <DrilledOrder selection={selection} />
            ) : (
                <PatientProfile
                    patient={patient}
                    returnFocusRxId={motion.returnFocusRxId}
                    onOpenOrder={selection.openOrder}
                />
            )}
        </PaneLayout>
    );
}

function PatientDetails({ selection, today }: { selection: PatientSelection; today: string }) {
    const { patient } = selection;
    if (patient.kind !== 'ready') return <PaneRestoreState restored={patient} label="patient" />;
    return <SelectionDetails selection={selection} patient={patient.value} today={today} />;
}

interface PatientsPanelProps {
    browse: PatientsBrowse;
    nav: IteroTabNav;
    today: string;
    onModeChange: (mode: DataTableMode) => void;
    onGoToOrders: () => void;
}

export function PatientsPanel({
    browse,
    nav,
    today,
    onModeChange,
    onGoToOrders
}: PatientsPanelProps) {
    const rows = tableRows(browse.patients, browse.errorMessage);
    const selection = usePatientSelection(nav, rows);
    const { selectedId } = selection;
    // Memoized so typing in the search box doesn't re-render the details pane.
    const details = useMemo(
        () => selectedId !== null && <PatientDetails selection={selection} today={today} />,
        [selectedId, selection, today]
    );
    const isPinned = useSelectionPin(selectedId, `${browse.listKey}:${browse.pagination.page}`);
    const openKey = visiblePaneKey(selectedId, rows, patientKey, isPinned);

    if (browse.isDisabled) return <PatientsDisabledState onGoToOrders={onGoToOrders} />;

    return (
        <BrowseBody
            detailsLabel="Patient details"
            details={details}
            openKey={openKey}
            onCloseDetails={selection.stepBack}
            list={
                <>
                    <IteroPatientsTable
                        key={browse.mode}
                        rows={rows}
                        layout={tableLayout(openKey)}
                        isLoading={browse.isLoading}
                        isUpdating={browse.isUpdating}
                        errorMessage={browse.errorMessage}
                        hasFilters={browse.hasFilters}
                        selectedId={selectedId}
                        sort={browse.sort}
                        listKey={browse.listKey}
                        mode={browse.mode}
                        onModeChange={onModeChange}
                        pagination={browse.pagination}
                        hasMore={browse.hasMore && browse.loadMoreError === null}
                        isLoadingMore={browse.isLoadingMore}
                        onLoadMore={browse.loadMore}
                        onSelect={selection.selectPatient}
                        onClearFilters={browse.clearFilters}
                        onRetry={browse.retry}
                    />
                    {browse.loadMoreError && (
                        <TableLoadMoreError
                            message={browse.loadMoreError}
                            onRetry={browse.loadMore}
                        />
                    )}
                </>
            }
        />
    );
}
